'use client'

import dynamic from 'next/dynamic'
import { alpha } from '@mui/material/styles'
import { Grid2 as Grid, Typography } from '@mui/material'
import moment from 'moment-jalaali'

// Types
import type { ArticleItemProps } from '@/types/components/molecules/items/article'

// Helpers
import { useTranslation } from '@/helpers/i18n/client'

// Utils
import ToPersianNumber from '@/utils/to-persian-number'
import Link from 'next/link'

// Components
const NorthWestArrowIconAtom = dynamic(() => import('@/components/atoms/icons/north-west-arrow'))
const ClockIconAtom = dynamic(() => import('@/components/atoms/icons/clock'))
const CalendarIconAtom = dynamic(() => import('@/components/atoms/icons/calendar'))
const BlackButtonAtom = dynamic(() => import('@/components/atoms/buttons/text/black'))

const ArticleItemMolecule = (props: ArticleItemProps) => {
	// Props
	const { title, alias, image, description, updated_date, created_date, category, fields_values, hovered, onMouseEnter, onMouseLeave } = props

	// Varaibles
	const { t } = useTranslation()

	// Calculate Minutes
	let words: number = description ? description.length : 0
	const minutes = Math.ceil(words / 400)

	return (
		<Grid size={12}>
			<Grid
				container
				justifyContent="space-between"
				position="relative"
				borderRadius={3}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
				sx={{
					height: { xs: 152, md: 258 },
					border: '1px solid #b8b8b8',
					transition: 'all .6s ease-in-out',
					overflow: 'hidden',
					'&::before, &::after': {
						opacity: 0,
						content: '""',
						width: 1,
						height: 1,
						position: 'absolute',
						top: 0,
						left: 0,
						transition: 'all .4s ease-in-out',
						...(hovered && { opacity: 1 })
					},
					'&::before': {
						backgroundImage: image ? `url(/api/download${image})` : 'url(/images/parts/article.png)',
						backgroundPosition: 'center',
						backgroundRepeat: 'no-repeat',
						backgroundSize: 'cover',
						zIndex: 1
					},
					'&::after': {
						backgroundColor: alpha('#000', 0.3),
						zIndex: 2
					},
					'& > .MuiGrid2-root': { zIndex: 3 },
					'& .MuiButtonBase-root': {
						px: 2.9,
						py: 2
					},
					'& .MuiSvgIcon-root': {
						width: 18,
						height: 18,
						mr: 1
					},
					...(hovered && {
						height: { xs: 277, md: 380 },
						borderColor: 'transparent',
						'& .article-title': {
							'& .MuiTypography-root ': {
								mt: { md: 4 },
								fontSize: { xs: 32, md: 40 },
								fontWeight: 800,
								lineHeight: { xs: 1.6, md: 1.2 }
							}
						},
						'& .article-text': { display: 'block' },
						'& .article-minute': { display: 'none' },
						'& .article-date': { display: 'none' },
						'& .article-button': {
							flexBasis: '100%',
							maxWidth: 1
						},
						'& .MuiButtonBase-root:not(:hover)': {
							color: 'primary.contrastText',
							bgcolor: 'primary.dark'
						},
						'& .MuiButtonBase-root:hover': { bgcolor: '#95a2ff' },
						'& .MuiTypography-root, & .MuiSvgIcon-root': { color: 'common.white' }
					})
				}}
			>
				<Grid size={12} px={3} position="absolute" top={24} left={0}>
					<Grid container direction={{ xs: 'column', md: 'row' }} justifyContent="space-between">
						<Grid className="article-title" size={12}>
							{title && (
								<Typography
									variant="h5"
									display="-webkit-box"
									fontSize={{ xs: 20, md: 16 }}
									fontWeight={700}
									lineHeight={1.6}
									letterSpacing={-0.2}
									textOverflow="ellipsis"
									color="#010101"
									overflow="hidden"
									sx={{
										WebkitBoxOrient: 'vertical',
										boxOrient: 'vertical',
										WebkitLineClamp: { xs: hovered ? 2 : 1, md: 2 },
										lineClamp: { xs: hovered ? 2 : 1, md: 2 },
										transition: 'all .4s ease-in-out'
									}}
								>
									{t(title)}
								</Typography>
							)}
						</Grid>

						{fields_values && (
							<Grid className="article-text" size={12} display={{ xs: 'none', md: 'block' }} mt={1}>
								<Typography
									variant="subtitle1"
									display="-webkit-box"
									fontSize={12.5}
									fontWeight={400}
									lineHeight={1.6}
									letterSpacing={-0.2}
									textOverflow="ellipsis"
									color="#7a7a7a"
									overflow="hidden"
									sx={{
										WebkitBoxOrient: 'vertical',
										boxOrient: 'vertical',
										WebkitLineClamp: hovered ? 3 : 2,
										lineClamp: hovered ? 3 : 2,
										transition: 'all .4s ease-in-out'
									}}
								>
									{t(fields_values[1].value || '')}
								</Typography>
							</Grid>
						)}

						<Grid className="article-minute" size={6} display="inline-flex" alignItems="center" mt={{ xs: 3, md: 2 }}>
							<ClockIconAtom />

							<Typography
								variant="subtitle2"
								color="#000"
								fontSize={{ xs: 12, md: 14 }}
								fontWeight={400}
								lineHeight={2}
								letterSpacing={-0.05}
								sx={{ transition: 'all .4s ease-in-out' }}
							>
								{t('form:label.duration')}: {t('form:label.minute', { minute: ToPersianNumber(minutes.toString()) })}
							</Typography>
						</Grid>

						<Grid className="article-date" size={6} display={{ xs: 'none', md: 'inline-flex' }} alignItems="center" mt={{ xs: 1, md: 2 }}>
							<CalendarIconAtom />

							<Typography variant="subtitle2" fontSize={{ xs: 16, md: 12.5 }} fontWeight={400} lineHeight={2} letterSpacing={-0.05} color="#000">
								{ToPersianNumber(moment(created_date).format('jYYYY-jMM-jDD'))}
							</Typography>
						</Grid>
					</Grid>
				</Grid>

				<Grid className="article-button" position="absolute" right={24} bottom={24}>
					<Link href={`/article/${alias}`}>
						<BlackButtonAtom title="form:button.readArticle" endIcon={<NorthWestArrowIconAtom />} />
					</Link>
				</Grid>
			</Grid>
		</Grid>
	)
}

export default ArticleItemMolecule
